import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter } from 'recharts';

const StopeGeology = ({ stopeID }) => {
  const BASE_URL = 'https://pickstonepresentation.dumisaminerals.com/server/api';
  const [geologyData, setGeologyData] = useState(null);
  const [benchSamplingData, setBenchSamplingData] = useState([]);
  const [developmentSamplingData, setDevelopmentSamplingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [benchSamplingLoading, setBenchSamplingLoading] = useState(false);
  const [developmentSamplingLoading, setDevelopmentSamplingLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [devCurrentPage, setDevCurrentPage] = useState(1);
  const [devItemsPerPage] = useState(10);
  const [xAxis, setXAxis] = useState('DateReported');
  const [yAxis, setYAxis] = useState('Grade');
  const [chartType, setChartType] = useState('bar');
  const [devXAxis, setDevXAxis] = useState('DateReported');
  const [devYAxis, setDevYAxis] = useState('Au');
  const [devChartType, setDevChartType] = useState('bar');

  // Pagination logic
  const totalPages = Math.ceil(benchSamplingData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = benchSamplingData.slice(startIndex, endIndex);

  // DevelopmentSampling pagination logic
  const devTotalPages = Math.ceil(developmentSamplingData.length / devItemsPerPage);
  const devStartIndex = (devCurrentPage - 1) * devItemsPerPage;
  const devEndIndex = devStartIndex + devItemsPerPage;
  const devCurrentData = developmentSamplingData.slice(devStartIndex, devEndIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [benchSamplingData]);

  useEffect(() => {
    setDevCurrentPage(1);
  }, [developmentSamplingData]);

  // Get unique values for dropdowns
  const getUniqueValues = (field) => {
    const values = [...new Set(benchSamplingData.map(item => item[field]).filter(val => val !== null && val !== undefined))];
    return values.sort();
  };

  // Get numeric columns for Y axis (excluding From, To, Distance, Depth)
  const getNumericColumns = () => {
    const numericFields = ['Grade', 'SampleWidth']; // Grade first as it's most commonly used
    return numericFields.filter(field => benchSamplingData.some(item => !isNaN(parseFloat(item[field]))));
  };

  // Get all columns for X axis (excluding StopeID, Level, Section, Distance, Depth, From, To, SampleWidth, Grade)
  const getAllColumns = () => {
    if (benchSamplingData.length === 0) return [];
    const excludedFields = ['StopeID', 'Level', 'Section', 'Distance', 'Depth', 'From', 'To', 'SampleWidth', 'Grade'];
    const availableFields = Object.keys(benchSamplingData[0]).filter(field => !excludedFields.includes(field));
    
    // Define preferred order for better UX
    const preferredOrder = [
      'DateReported',
      'Location', 
      'SampleType',
      'Letter',
      'Direction',
      'PegID',
      'SampleID',
      'Sampler',
      'Geotech'
    ];
    
    // Sort fields according to preferred order, then add any remaining fields
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Add any remaining fields not in preferred order
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Get all columns for DevelopmentSampling X axis (excluding StopeID, Level, Section, Distance, From, To, SampleWidth, Au)
  const getDevAllColumns = () => {
    if (developmentSamplingData.length === 0) return [];
    const excludedFields = ['StopeID', 'Level', 'Section', 'Distance', 'From', 'To', 'SampleWidth', 'Au'];
    const availableFields = Object.keys(developmentSamplingData[0]).filter(field => !excludedFields.includes(field));
    
    // Define preferred order for better UX
    const preferredOrder = [
      'DateReported',
      'HoleID',
      'Location', 
      'SampleType',
      'Direction',
      'PegID',
      'STD',
      'SampleID',
      'Sampler',
      'Geotech',
      'RCH NUMBER'
    ];
    
    // Sort fields according to preferred order, then add any remaining fields
    const orderedFields = [];
    preferredOrder.forEach(field => {
      if (availableFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    // Add any remaining fields not in preferred order
    availableFields.forEach(field => {
      if (!orderedFields.includes(field)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  // Format date for better readability
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // Return original if invalid
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateStr; // Return original if parsing fails
    }
  };

  // Process data for chart (handle averaging for grade)
  const processChartData = () => {
    if (!benchSamplingData.length) return [];

    const grouped = {};
    const originalDates = {}; // Store original dates for sorting
    
    benchSamplingData.forEach(item => {
      let key = item[xAxis];
      let originalDate = null;
      
      // Format date if X-axis is DateReported
      if (xAxis === 'DateReported') {
        originalDate = item[xAxis]; // Store original date
        key = formatDate(key);
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [xAxis]: key,
          count: 0,
          totalGrade: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add all numeric fields
      getNumericColumns().forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [xAxis]: group[xAxis], count: group.count };
      
      getNumericColumns().forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Special handling for SampleWidth - use sum instead of average
          if (field === 'SampleWidth') {
            processed[field] = total; // Use sum for SampleWidth
          } else {
            const average = total / group.count;
            // Format Grade to 2 decimal places
            if (field === 'Grade') {
              processed[field] = parseFloat(average.toFixed(2));
            } else {
              processed[field] = average;
            }
          }
          processed[`${field}_total`] = total; // Sum
        }
      });
      
      return processed;
    });

    // Sort by date if X-axis is DateReported
    if (xAxis === 'DateReported') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[xAxis]]);
        const dateB = new Date(originalDates[b[xAxis]]);
        return dateA - dateB; // Ascending order
      });
    }

    return processedData;
  };

  const chartData = processChartData();

  // DevelopmentSampling chart data processing
  const processDevelopmentChartData = () => {
    if (!developmentSamplingData.length) return [];

    const grouped = {};
    const originalDates = {}; // Store original dates for sorting
    
    developmentSamplingData.forEach(item => {
      let key = item[devXAxis];
      let originalDate = null;
      
      // Format date if X-axis is DateReported
      if (devXAxis === 'DateReported') {
        originalDate = item[devXAxis]; // Store original date
        key = formatDate(key);
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          [devXAxis]: key,
          count: 0,
          totalAu: 0,
          items: []
        };
        if (originalDate) {
          originalDates[key] = originalDate;
        }
      }
      
      grouped[key].count++;
      grouped[key].items.push(item);
      
      // Add all numeric fields
      const numericFields = ['Au', 'SampleWidth', 'Distance', 'From', 'To'];
      numericFields.forEach(field => {
        const value = parseFloat(item[field]);
        if (!isNaN(value)) {
          if (!grouped[key][`total_${field}`]) grouped[key][`total_${field}`] = 0;
          grouped[key][`total_${field}`] += value;
        }
      });
    });

    // Calculate averages and return processed data
    const processedData = Object.values(grouped).map(group => {
      const processed = { [devXAxis]: group[devXAxis], count: group.count };
      
      const numericFields = ['Au', 'SampleWidth', 'Distance', 'From', 'To'];
      numericFields.forEach(field => {
        const total = group[`total_${field}`];
        if (total !== undefined) {
          // Special handling for SampleWidth - use sum instead of average
          if (field === 'SampleWidth') {
            processed[field] = total; // Use sum for SampleWidth
          } else {
            const average = total / group.count;
            // Format Au to 2 decimal places
            if (field === 'Au') {
              processed[field] = parseFloat(average.toFixed(2));
            } else {
              processed[field] = average;
            }
          }
          processed[`${field}_total`] = total; // Sum
        }
      });
      
      return processed;
    });

    // Sort by date if X-axis is DateReported
    if (devXAxis === 'DateReported') {
      return processedData.sort((a, b) => {
        const dateA = new Date(originalDates[a[devXAxis]]);
        const dateB = new Date(originalDates[b[devXAxis]]);
        return dateA - dateB; // Ascending order
      });
    }

    return processedData;
  };

  const devChartData = processDevelopmentChartData();

  // Filter bench sampling data by stopeID
  const filterBenchSamplingByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      // Database StopeID (shorter) should be the needle
      // Parameter stopeID (longer) is the haystack
      const databaseStopeID = item.StopeID; // e.g., "5L_W2"
      const parameterStopeID = stopeID; // e.g., "5L_W2B_STP"
      
      // Check if parameter stopeID starts with database StopeID
      if (parameterStopeID.startsWith(databaseStopeID)) {
        // Get the character right after the database StopeID in the parameter
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        
        // If there's no next character (exact match) or next character is not a number, include it
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  // Filter development sampling data by stopeID
  const filterDevelopmentSamplingByStope = (data) => {
    if (!stopeID || !data.length) return data;
    
    return data.filter(item => {
      if (!item.StopeID) return false;
      
      // Database StopeID (shorter) should be the needle
      // Parameter stopeID (longer) is the haystack
      const databaseStopeID = item.StopeID; // e.g., "5L_W2"
      const parameterStopeID = stopeID; // e.g., "5L_W2B_STP"
      
      // Check if parameter stopeID starts with database StopeID
      if (parameterStopeID.startsWith(databaseStopeID)) {
        // Get the character right after the database StopeID in the parameter
        const nextChar = parameterStopeID.charAt(databaseStopeID.length);
        
        // If there's no next character (exact match) or next character is not a number, include it
        if (!nextChar || isNaN(parseInt(nextChar))) {
          return true;
        }
      }
      
      return false;
    });
  };

  useEffect(() => {
    if (stopeID) {
      setLoading(true);
      setBenchSamplingLoading(true);
      setDevelopmentSamplingLoading(true);

      // Fetch bench sampling data
      const fetchBenchSamplingData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/benchsampling`);
          if (!response.ok) throw new Error('Failed to fetch bench sampling data');
          
          const result = await response.json();
          const filteredData = filterBenchSamplingByStope(result);
          
          console.log('Bench sampling - Original data count:', result.length);
          console.log('Bench sampling - Filtered data count:', filteredData.length);
          console.log('Bench sampling - Parameter stopeID (haystack):', stopeID);
          console.log('Bench sampling - Sample database StopeIDs:', result.slice(0, 5).map(item => item.StopeID));
          
          setBenchSamplingData(filteredData);
          
          // Generate summary data from actual bench sampling data
          if (filteredData.length > 0) {
            const grades = filteredData.map(item => parseFloat(item.Grade)).filter(grade => !isNaN(grade));
            const dates = filteredData.map(item => new Date(item.DateReported)).filter(date => !isNaN(date.getTime()));
            
            const summaryData = {
              sampleCount: filteredData.length,
              averageGrade: grades.length > 0 ? (grades.reduce((sum, grade) => sum + grade, 0) / grades.length).toFixed(2) : 'N/A',
              minDate: dates.length > 0 ? formatDate(new Date(Math.min(...dates))) : 'N/A',
              maxDate: dates.length > 0 ? formatDate(new Date(Math.max(...dates))) : 'N/A',
              gradeRange: grades.length > 0 ? `${Math.min(...grades).toFixed(2)} - ${Math.max(...grades).toFixed(2)}` : 'N/A'
            };
            
            setGeologyData(summaryData);
          } else {
            setGeologyData({
              sampleCount: 0,
              averageGrade: 'N/A',
              minDate: 'N/A',
              maxDate: 'N/A',
              gradeRange: 'N/A'
            });
          }
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching bench sampling data:', err);
          setBenchSamplingData([]);
          setGeologyData({
            sampleCount: 0,
            averageGrade: 'N/A',
            minDate: 'N/A',
            maxDate: 'N/A',
            gradeRange: 'N/A'
          });
          setLoading(false);
        } finally {
          setBenchSamplingLoading(false);
        }
      };

      // Fetch development sampling data
      const fetchDevelopmentSamplingData = async () => {
        try {
          const response = await fetch(`${BASE_URL}/developmentsampling`);
          if (!response.ok) throw new Error('Failed to fetch development sampling data');
          
          const result = await response.json();
          const filteredData = filterDevelopmentSamplingByStope(result);
          
          console.log('Development sampling - Original data count:', result.length);
          console.log('Development sampling - Filtered data count:', filteredData.length);
          
          setDevelopmentSamplingData(filteredData);
        } catch (err) {
          console.error('Error fetching development sampling data:', err);
          setDevelopmentSamplingData([]);
        } finally {
          setDevelopmentSamplingLoading(false);
        }
      };

      fetchBenchSamplingData();
      fetchDevelopmentSamplingData();
    }
  }, [stopeID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-2 text-gray-600">Loading geology data...</span>
      </div>
    );
  }

  if (!geologyData) {
    return (
      <div className="text-center p-8 text-gray-500">
        <p>No geology data available for this stope.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-semibold text-gray-700 mb-2">Sample Count</h4>
          <p className="text-gray-600 text-lg font-medium">{geologyData.sampleCount}</p>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-semibold text-gray-700 mb-2">Average Grade</h4>
          <p className="text-gray-600 text-lg font-medium">{geologyData.averageGrade}</p>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-semibold text-gray-700 mb-2">Date Range</h4>
          <p className="text-gray-600">{geologyData.minDate} to {geologyData.maxDate}</p>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-lg">
          <h4 className="font-semibold text-gray-700 mb-2">Grade Range</h4>
          <p className="text-gray-600">{geologyData.gradeRange}</p>
        </div>
      </div>

      {/* Bench Sampling Table */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Bench Sampling Data</h3>
        
        {benchSamplingLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading bench sampling data...</span>
          </div>
        ) : benchSamplingData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">StopeID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Level</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Section</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Location</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">SampleType</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Distance</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Grade</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Sampler</th>
                </tr>
              </thead>
              <tbody>
                 {currentData.map((item, index) => {
                   // Format date for better readability
                   const formatDate = (dateStr) => {
                     if (!dateStr) return 'N/A';
                     try {
                       const date = new Date(dateStr);
                       if (isNaN(date.getTime())) return dateStr; // Return original if invalid
                       return date.toLocaleDateString('en-US', {
                         year: 'numeric',
                         month: 'short',
                         day: 'numeric'
                       });
                     } catch (error) {
                       return dateStr; // Return original if parsing fails
                     }
                   };

                   return (
                     <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                       <td className="px-3 py-2">{formatDate(item.DateReported)}</td>
                       <td className="px-3 py-2">{item.StopeID}</td>
                       <td className="px-3 py-2">{item.Level}</td>
                       <td className="px-3 py-2">{item.Section}</td>
                       <td className="px-3 py-2">{item.Location}</td>
                       <td className="px-3 py-2">{item.SampleType}</td>
                       <td className="px-3 py-2">{item.Distance}</td>
                       <td className="px-3 py-2">{item.Grade ? parseFloat(item.Grade).toFixed(2) : 'N/A'}</td>
                       <td className="px-3 py-2">{item.Sampler}</td>
                     </tr>
                   );
                 })}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            {benchSamplingData.length > itemsPerPage && (
              <div className="flex items-center justify-center space-x-4 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <span className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No bench sampling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Interactive Graph Section */}
      {benchSamplingData.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Data Visualization</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={xAxis} 
                  onChange={(e) => setXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={yAxis} 
                  onChange={(e) => setYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="count">Count</option>
                  {getNumericColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={chartType} 
                  onChange={(e) => setChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setXAxis('DateReported');
                    setYAxis('Grade');
                    setChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'bar' && (
                  <BarChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={yAxis} fill="#f59e0b" />
                  </BarChart>
                )}
                
                {chartType === 'line' && (
                  <LineChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={yAxis} stroke="#f59e0b" strokeWidth={2} />
                  </LineChart>
                )}
                
                {chartType === 'scatter' && (
                  <ScatterChart 
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={xAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={yAxis} fill="#f59e0b" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Development Sampling Table */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Development Sampling Data</h3>
        
        {developmentSamplingLoading ? (
          <div className="flex items-center justify-center p-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
            <span className="ml-2 text-gray-600">Loading development sampling data...</span>
          </div>
        ) : developmentSamplingData.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">HoleID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">StopeID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Level</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Location</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">SampleType</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Distance</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Au</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Sampler</th>
                </tr>
              </thead>
              <tbody>
                {devCurrentData.map((item, index) => {
                  // Format date for better readability
                  const formatDate = (dateStr) => {
                    if (!dateStr) return 'N/A';
                    try {
                      const date = new Date(dateStr);
                      if (isNaN(date.getTime())) return dateStr; // Return original if invalid
                      return date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } catch (error) {
                      return dateStr; // Return original if parsing fails
                    }
                  };

                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2">{formatDate(item.DateReported)}</td>
                      <td className="px-3 py-2">{item.HoleID}</td>
                      <td className="px-3 py-2">{item.StopeID}</td>
                      <td className="px-3 py-2">{item.Level}</td>
                      <td className="px-3 py-2">{item.Location}</td>
                      <td className="px-3 py-2">{item.SampleType}</td>
                      <td className="px-3 py-2">{item.Distance}</td>
                      <td className="px-3 py-2">{item.Au ? parseFloat(item.Au).toFixed(2) : 'N/A'}</td>
                      <td className="px-3 py-2">{item.Sampler}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* DevelopmentSampling Pagination Controls */}
            {developmentSamplingData.length > devItemsPerPage && (
              <div className="flex items-center justify-center space-x-4 p-2 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setDevCurrentPage(1)}
                  disabled={devCurrentPage === 1}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                
                <button
                  onClick={() => setDevCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={devCurrentPage === 1}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <span className="text-xs text-gray-500">
                  Page {devCurrentPage} of {devTotalPages}
                </span>
                
                <button
                  onClick={() => setDevCurrentPage(prev => Math.min(prev + 1, devTotalPages))}
                  disabled={devCurrentPage === devTotalPages}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                
                <button
                  onClick={() => setDevCurrentPage(devTotalPages)}
                  disabled={devCurrentPage === devTotalPages}
                  className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 text-gray-500">
            <p>No development sampling data available for this stope.</p>
          </div>
        )}
      </div>

      {/* Development Sampling Interactive Graph Section */}
      {developmentSamplingData.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Development Sampling Visualization</h3>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            {/* Chart Controls */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">X Axis</label>
                <select 
                  value={devXAxis} 
                  onChange={(e) => setDevXAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {getDevAllColumns().map(column => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Y Axis</label>
                <select 
                  value={devYAxis} 
                  onChange={(e) => setDevYAxis(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="count">Count</option>
                  <option value="Au">Au</option>
                  <option value="SampleWidth">SampleWidth</option>
                  <option value="Distance">Distance</option>
                  <option value="From">From</option>
                  <option value="To">To</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                <select 
                  value={devChartType} 
                  onChange={(e) => setDevChartType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="scatter">Scatter Plot</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button 
                  onClick={() => {
                    setDevXAxis('DateReported');
                    setDevYAxis('Au');
                    setDevChartType('bar');
                  }}
                  className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Chart */}
            <div className="w-full" style={{ height: '500px', minHeight: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                {devChartType === 'bar' && (
                  <BarChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={devYAxis} fill="#f59e0b" />
                  </BarChart>
                )}
                
                {devChartType === 'line' && (
                  <LineChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey={devYAxis} stroke="#f59e0b" strokeWidth={2} />
                  </LineChart>
                )}
                
                {devChartType === 'scatter' && (
                  <ScatterChart 
                    data={devChartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 50
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey={devXAxis} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      fontSize={11}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Scatter dataKey={devYAxis} fill="#f59e0b" />
                  </ScatterChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StopeGeology;
